import { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import {
  MAX_PINS,
  PREFERENCES_CHANGED_EVENT,
  PinResult,
  PinnedProduct,
  ProductPreferenceDocument,
  pinProduct,
  prunePreferences,
  readPreferences,
  setLastSku as persistLastSku,
  unpinProduct,
} from '../utils/product-preferences';

export interface UseProductPreferencesResult {
  lastSku: string | null;
  pins: PinnedProduct[];
  setLastSku: (sku: string) => void;
  pin: (product: PinnedProduct) => PinResult;
  unpin: (sku: string) => void;
  pruneToCatalog: (validSkus: string[]) => void;
  maxPins: number;
}

function applyDoc(
  setLastSkuState: (v: string | null) => void,
  setPins: (v: PinnedProduct[]) => void,
  doc: ProductPreferenceDocument
): void {
  setLastSkuState(doc.lastSku);
  setPins(doc.pins);
}

/**
 * React binding for per-user product preference storage.
 * Initial state is read synchronously so first paint can restore lastSku.
 * Listens for same-tab preference changes so Sidebar updates immediately on pin.
 */
export function useProductPreferences(): UseProductPreferencesResult {
  const { account } = useAuth();
  const userKey = account?.username ?? null;

  const [lastSku, setLastSkuState] = useState<string | null>(
    () => readPreferences(userKey).lastSku
  );
  const [pins, setPins] = useState<PinnedProduct[]>(
    () => readPreferences(userKey).pins
  );

  const rehydrate = useCallback(() => {
    applyDoc(setLastSkuState, setPins, readPreferences(userKey));
  }, [userKey]);

  useEffect(() => {
    rehydrate();
  }, [rehydrate]);

  useEffect(() => {
    const onChange = (): void => {
      rehydrate();
    };
    window.addEventListener(PREFERENCES_CHANGED_EVENT, onChange);
    return () => {
      window.removeEventListener(PREFERENCES_CHANGED_EVENT, onChange);
    };
  }, [rehydrate]);

  const setLastSku = useCallback(
    (sku: string) => {
      applyDoc(setLastSkuState, setPins, persistLastSku(userKey, sku));
    },
    [userKey]
  );

  const pin = useCallback(
    (product: PinnedProduct): PinResult => {
      const result = pinProduct(userKey, product);
      if (result.ok) {
        applyDoc(setLastSkuState, setPins, result.document);
      }
      return result;
    },
    [userKey]
  );

  const unpin = useCallback(
    (sku: string) => {
      applyDoc(setLastSkuState, setPins, unpinProduct(userKey, sku));
    },
    [userKey]
  );

  const pruneToCatalog = useCallback(
    (validSkus: string[]) => {
      applyDoc(
        setLastSkuState,
        setPins,
        prunePreferences(userKey, new Set(validSkus))
      );
    },
    [userKey]
  );

  return {
    lastSku,
    pins,
    setLastSku,
    pin,
    unpin,
    pruneToCatalog,
    maxPins: MAX_PINS,
  };
}
